import React, { useMemo } from 'react';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { findSoundsInString } from '@/utils/sounds';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity } from '../Activity';
import { ClayMatchStep } from '../components/ClayMatchStep';
import { ActivityTypeProps } from '../types';
import { getStepNumber } from '../utils';

export const ReadingFocusSounds = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  const { getLessonByActivity, getEncounteredLetters } = useLessonPlayer();

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const availableSounds = useMemo(() => {
    const lesson = getLessonByActivity(activity);

    if (!lesson) {
      return;
    }

    return getEncounteredLetters(lesson.id);
  }, [activity, getEncounteredLetters, getLessonByActivity]);

  const sounds = useMemo(() => {
    if (!availableSounds) {
      return;
    }

    if (!activity.sounds) {
      return;
    }

    if (!soundsData) {
      return;
    }

    return findSoundsInString(availableSounds, activity.sounds);
  }, [activity, soundsData, availableSounds]);

  const NUMBER_OF_STEPS_PER_SOUND = 1;

  return (
    sounds && (
      <Activity
        steps={[
          <ActivityTitleStep
            key={`${activity.id}-title`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={0}
            total={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
          />,
          ...sounds?.flatMap((sound, index) =>
            availableSounds
              ? [
                  <ClayMatchStep
                    key={`${activity.id}-${index}-1`}
                    stepIdentifier={`${activity.id}-${index}-1`}
                    activity={activity}
                    sound={sound}
                    availableSounds={availableSounds}
                    stepNumber={getStepNumber(
                      index + 1,
                      0,
                      NUMBER_OF_STEPS_PER_SOUND,
                    )}
                    totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
                  />,
                ]
              : [],
          ),
        ]}
      />
    )
  );
};
